import React, { useState, useEffect } from "react";
import { Loader, Table, Dropdown, Icon, Button, Label } from "semantic-ui-react";
import { map, size, sumBy, filter } from "lodash";
import moment from "moment";
import { toast } from "react-toastify";
import { HeaderPage, PrintHeader, PrintTable } from "../../components/Admin";
import { useInvoicing, usePrintOrientation } from "../../hooks";
import { getBusinessToday } from "../../utils/date";

const money = (value) => Number(value || 0).toFixed(2);

// No "": el Dropdown de Semantic trata el string vacío como "sin selección".
const ALL = "__all__";
const TYPE_OPTIONS = [
  { key: "all", value: ALL, text: "Todos" },
  { key: "BOLETA", value: "BOLETA", text: "Boletas" },
  { key: "FACTURA", value: "FACTURA", text: "Facturas" },
  { key: "NOTA", value: "NOTA", text: "Notas de venta" },
];
const STATUS_OPTIONS = [
  { key: "all", value: ALL, text: "Todos" },
  { key: "ACCEPTED", value: "ACCEPTED", text: "Aceptados" },
  { key: "VOIDED", value: "VOIDED", text: "Anulados" },
  { key: "REJECTED", value: "REJECTED", text: "Rechazados" },
  { key: "PENDING", value: "PENDING", text: "Pendientes" },
];
const STATUS_TEXT = {
  ACCEPTED: ["Aceptado", "green"],
  VOIDED: ["Anulado", "red"],
  REJECTED: ["Rechazado", "orange"],
  PENDING: ["Pendiente", "yellow"],
};
// Lo que cuenta en los totales: lo vigente (no anulado ni rechazado).
const counts = (row) => row.status === "ACCEPTED" || row.status === "PENDING";

// Caja → Comprobantes: reporte de boletas y facturas emitidas, por día
// operativo, con descarga del PDF de cada una y exportación para el contador.
export function ComprobantesReport() {
  const { loading, comprobantes, getComprobantes, downloadComprobantePdf } = useInvoicing();
  const [dateFrom, setDateFrom] = useState(getBusinessToday);
  const [dateTo, setDateTo] = useState(getBusinessToday);
  const [docType, setDocType] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  usePrintOrientation("landscape");

  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    getComprobantes({
      date_from: dateFrom,
      date_to: dateTo,
      ...(docType !== ALL ? { doc_type: docType } : {}),
      ...(status !== ALL ? { status } : {}),
    });
  }, [dateFrom, dateTo, docType, status]);

  // Los totales fiscales (boleta + factura) van separados de las notas de venta
  // internas: no se mezclan en lo que se declara.
  const valid = filter(comprobantes, (row) => counts(row) && row.doc_type !== "NOTA");
  const notes = filter(comprobantes, (row) => counts(row) && row.doc_type === "NOTA");
  const notesTotal = sumBy(notes, (row) => Number(row.total));
  const totals = {
    count: size(valid),
    taxable: sumBy(valid, (row) => Number(row.taxable_amount)),
    igv: sumBy(valid, (row) => Number(row.igv_amount)),
    total: sumBy(valid, (row) => Number(row.total)),
  };
  const rangeLabel = `Del ${moment(dateFrom).format("DD/MM/YYYY")} al ${moment(dateTo).format(
    "DD/MM/YYYY"
  )}`;
  const when = (value) => moment.parseZone(value).format("DD/MM/YY HH:mm");

  const onDownload = async (row) => {
    try {
      await downloadComprobantePdf(row);
    } catch (error) {
      toast.error(error.message);
    }
  };

  // CSV con BOM para que Excel respete las tildes; el contador lo abre directo.
  const exportCsv = () => {
    const header = [
      "Fecha", "Tipo", "Serie-Número", "Cliente", "Documento", "N° documento",
      "Op. gravada", "IGV", "Total", "Estado", "Prueba",
    ];
    const rows = map(comprobantes, (row) => [
      when(row.issued_at), row.doc_label, row.full_number, row.customer_name,
      row.customer_doc_type === "NONE" ? "" : row.customer_doc_type, row.customer_doc_number,
      money(row.taxable_amount), money(row.igv_amount), money(row.total),
      STATUS_TEXT[row.status]?.[0] || row.status, row.is_test ? "Sí" : "No",
    ]);
    const csv = [header, ...rows]
      .map((cols) => cols.map((col) => `"${String(col ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\r\n");
    const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `comprobantes_${dateFrom}_${dateTo}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <>
      <div className="screen-only">
        <HeaderPage
          title="Caja — Comprobantes"
          btnTitle={
            <>
              <Icon name="print" /> Imprimir
            </>
          }
          btnClick={() => window.print()}
        />
      </div>

      <div className="screen-only" style={{ display: "flex", gap: 15, flexWrap: "wrap", margin: "10px 0 20px" }}>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Desde</label>
          <input type="date" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Hasta</label>
          <input type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Tipo</label>
          <Dropdown selection options={TYPE_OPTIONS} value={docType} onChange={(_, d) => setDocType(d.value)} />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Estado</label>
          <Dropdown selection options={STATUS_OPTIONS} value={status} onChange={(_, d) => setStatus(d.value)} />
        </div>
        <div style={{ alignSelf: "flex-end" }}>
          <Button basic onClick={exportCsv} disabled={size(comprobantes) === 0}>
            <Icon name="file excel outline" /> Exportar (Excel)
          </Button>
        </div>
      </div>

      <PrintTable>
        <PrintHeader title="Comprobantes emitidos" subtitle={rangeLabel} />

        {loading ? (
          <Loader active inline="centered">
            Cargando...
          </Loader>
        ) : size(comprobantes) === 0 ? (
          <p className="print-content">No hay comprobantes en este rango.</p>
        ) : (
          <div className="print-content">
            <Table compact unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Fecha</Table.HeaderCell>
                  <Table.HeaderCell>Comprobante</Table.HeaderCell>
                  <Table.HeaderCell>Cliente</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Op. gravada</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">IGV</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Total (S/)</Table.HeaderCell>
                  <Table.HeaderCell>Estado</Table.HeaderCell>
                  <Table.HeaderCell className="screen-only" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {map(comprobantes, (row) => {
                  const [statusText, statusColor] = STATUS_TEXT[row.status] || [row.status, "grey"];
                  return (
                    <Table.Row key={row.id} negative={row.status === "VOIDED"}>
                      <Table.Cell>{when(row.issued_at)}</Table.Cell>
                      <Table.Cell>
                        <strong>{row.full_number}</strong>
                        <div style={{ fontSize: "0.85em", color: "#777" }}>{row.doc_label}</div>
                      </Table.Cell>
                      <Table.Cell>
                        {row.customer_name || "CLIENTES VARIOS"}
                        {row.customer_doc_number && (
                          <div style={{ fontSize: "0.85em", color: "#777" }}>
                            {row.customer_doc_type} {row.customer_doc_number}
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell textAlign="right">{money(row.taxable_amount)}</Table.Cell>
                      <Table.Cell textAlign="right">{money(row.igv_amount)}</Table.Cell>
                      <Table.Cell textAlign="right">{money(row.total)}</Table.Cell>
                      <Table.Cell>
                        <Label size="tiny" color={statusColor}>
                          {statusText}
                        </Label>
                        {row.is_test && (
                          <Label size="tiny" basic color="yellow">
                            PRUEBA
                          </Label>
                        )}
                      </Table.Cell>
                      <Table.Cell className="screen-only" textAlign="right">
                        <Button basic size="mini" onClick={() => onDownload(row)} title="Descargar PDF">
                          <Icon name="file pdf outline" /> PDF
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
              <Table.Footer>
                <Table.Row>
                  <Table.HeaderCell colSpan="3">
                    Total fiscal vigente ({totals.count} boleta/factura{totals.count === 1 ? "" : "s"}, sin
                    anulados ni rechazados)
                  </Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">{money(totals.taxable)}</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">{money(totals.igv)}</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">{money(totals.total)}</Table.HeaderCell>
                  <Table.HeaderCell colSpan="2" />
                </Table.Row>
                {size(notes) > 0 && (
                  <Table.Row>
                    <Table.HeaderCell colSpan="5">
                      Notas de venta internas ({size(notes)}, no fiscales)
                    </Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">{money(notesTotal)}</Table.HeaderCell>
                    <Table.HeaderCell colSpan="2" />
                  </Table.Row>
                )}
              </Table.Footer>
            </Table>
          </div>
        )}
      </PrintTable>
    </>
  );
}
